import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Feather, ImageDown, Instagram, Music, Video } from "lucide-react";
import { toast } from "sonner";
import { INSTAGRAM_URL, poems, songs } from "../../data/content";
import { sharePoemImage } from "../../lib/sharePoem";
import { goToVideoPoem, scrollToSection } from "../../lib/scroll";
import Modal from "./Modal";

const lines = [
    { text: "Aún hay", accent: false },
    { text: "fotografías", accent: true },
    { text: "que se hacen", accent: false },
    { text: "con palabras.", accent: false },
];

const Hero = () => {
    const { scrollY } = useScroll();
    const yBack = useTransform(scrollY, [0, 900], [0, 60]);
    const yFront = useTransform(scrollY, [0, 900], [0, -80]);
    const yPortrait = useTransform(scrollY, [0, 900], [0, -130]);
    const fade = useTransform(scrollY, [0, 500], [1, 0]);
    const [poemOpen, setPoemOpen] = useState(false);

    const now = new Date();
    const featured = poems[(now.getMonth() + now.getFullYear()) % poems.length];
    const monthName = now.toLocaleDateString("es-EC", { month: "long" });
    const [sharing, setSharing] = useState(false);
    const featuredSong = songs.find((s) => s.poemId === featured.id);

    const listenFeaturedSong = () => {
        if (!featuredSong) return;
        scrollToSection("#canciones");
        window.dispatchEvent(new CustomEvent("play-song", { detail: featuredSong.id }));
    };

    const shareFeatured = async () => {
        setSharing(true);
        try {
            const result = await sharePoemImage(featured);
            if (result === "shared") {
                toast.success("Imagen lista: elige «Guardar imagen» para tenerla en tu galería");
            } else {
                toast.success("Imagen descargada: búscala en tu carpeta de Descargas");
            }
        } catch (err) {
            if (err?.name !== "AbortError") toast.error("No se pudo generar la imagen");
        } finally {
            setSharing(false);
        }
    };

    return (
        <section
            id="inicio"
            data-testid="hero-section"
            className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-16"
        >
            <img
                src="/media/trazo-camara-detalle.png"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute -left-6 bottom-6 w-36 sm:w-52 opacity-[0.07] rotate-[-6deg]"
            />
            <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7">
                    <h1
                        data-testid="hero-title"
                        className="mt-8 font-display font-light tracking-tight text-ink leading-[1.14] text-5xl sm:text-7xl xl:text-8xl"
                    >
                        {lines.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-2">
                                <motion.span
                                    className={`block ${line.accent ? "italic text-crimson" : ""}`}
                                    initial={{ y: "115%" }}
                                    animate={{ y: 0 }}
                                    transition={{
                                        delay: 0.35 + i * 0.13,
                                        duration: 1,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                >
                                    {i === 0 && "«"}
                                    {line.text}
                                    {i === lines.length - 1 && "»"}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        className="mt-8 max-w-md text-base sm:text-lg leading-relaxed text-subtle"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.05, duration: 0.8 }}
                    >
                        Cuatro libros publicados y dos décadas haciendo fotografías
                        con palabras. Hoy, mis versos abandonan el papel para
                        transformarse en vídeo poesía. Una obra que se lee, se escucha
                        y se mira
                    </motion.p>

                    <motion.div
                        className="mt-10 flex flex-wrap items-center gap-4"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.2, duration: 0.8 }}
                    >
                        <button
                            data-testid="hero-cta-books"
                            onClick={() => scrollToSection("#poemas")}
                            className="group flex items-center gap-3 bg-ink text-paper px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-crimson transition-colors duration-300"
                        >
                            Leer los poemas
                            <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-1" />
                        </button>
                        <a
                            data-testid="hero-cta-instagram"
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center gap-3 border border-ink px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink hover:bg-ink hover:text-paper transition-colors duration-300"
                        >
                            <Instagram size={14} />
                            Vídeo poesía
                        </a>
                    </motion.div>

                    <motion.div
                        data-testid="hero-featured-poem"
                        className="ml-auto mr-2 sm:mr-10 mt-12 max-w-md border border-line bg-cream p-6 sm:p-8 text-center shadow-xl"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.35, duration: 0.8 }}
                    >
                        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-crimson">
                            ✳ Poema del mes · {monthName} ✳
                        </p>
                        <p className="mt-3 font-display text-3xl text-ink">
                            {featured.title}
                        </p>
                        <p className="mt-3 font-display italic text-lg leading-snug text-ink/70">
                            {featured.lines.filter(Boolean).slice(0, 2).join(" / ")}…
                        </p>
                        <button
                            data-testid="hero-featured-poem-open"
                            onClick={() => setPoemOpen(true)}
                            className="mx-auto mt-4 flex items-center gap-2 border-b border-ink pb-1 font-mono text-[10px] uppercase tracking-[0.22em] text-ink hover:text-crimson hover:border-crimson transition-colors"
                        >
                            <Feather size={12} />
                            Leer poema completo
                        </button>
                        {featuredSong && (
                            <button
                                data-testid="hero-featured-song-link"
                                onClick={listenFeaturedSong}
                                className="mx-auto mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-crimson hover:text-ink transition-colors"
                            >
                                <Music size={12} />
                                Escuchar la canción
                            </button>
                        )}
                        {featured.videoId && (
                            <button
                                data-testid="hero-featured-video-link"
                                onClick={() => goToVideoPoem(featured.videoId)}
                                className="mx-auto mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-golddeep hover:text-ink transition-colors"
                            >
                                <Video size={12} />
                                Ver vídeo poema
                            </button>
                        )}
                    </motion.div>

                    <motion.div
                        data-testid="hero-portrait-mobile"
                        className="mt-10 lg:hidden text-right"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.5, duration: 0.8 }}
                    >
                        <div className="inline-block rotate-[-3deg] border border-line bg-white p-2 pb-3 shadow-lg">
                            <img
                                src="/media/retrato.webp"
                                alt="Retrato de José Luis Garcés Maldonado"
                                className="w-32 h-40 object-cover"
                            />
                            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                                El autor · Quito
                            </p>
                        </div>
                    </motion.div>
                </div>

                <div className="hidden lg:block lg:col-span-5 relative h-[560px]">
                    <motion.div
                        style={{ y: yBack }}
                        className="absolute right-0 top-24 w-48 rotate-[7deg] border border-line bg-white p-3 shadow-xl"
                        initial={{ opacity: 0, y: 60, rotate: 12 }}
                        animate={{ opacity: 1, y: 0, rotate: 7 }}
                        transition={{ delay: 0.95, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <img
                            src="/covers/mujer.png"
                            alt="Portada de Mujer Ciudad"
                            className="w-full h-60 object-contain"
                        />
                        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                            Mujer Ciudad — 2016
                        </p>
                    </motion.div>
                    <motion.div
                        style={{ y: yFront }}
                        className="absolute left-0 top-8 w-52 rotate-[-6deg] border border-line bg-white p-3 shadow-xl"
                        initial={{ opacity: 0, y: 60, rotate: -12 }}
                        animate={{ opacity: 1, y: 0, rotate: -6 }}
                        transition={{ delay: 0.75, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <img
                            src="/media/instantaneas-real.webp"
                            alt="Portada de Instantáneas"
                            className="w-full h-64 object-cover"
                        />
                        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                            Instantáneas — 2012
                        </p>
                    </motion.div>
                    <motion.div
                        data-testid="hero-portrait"
                        style={{ y: yPortrait }}
                        className="absolute left-1/2 top-44 z-10 w-60 -translate-x-1/2 rotate-[-2deg] border border-line bg-white p-3 pb-4 shadow-2xl"
                        initial={{ opacity: 0, y: 100, rotate: -8 }}
                        animate={{ opacity: 1, y: 0, rotate: -2 }}
                        transition={{ delay: 0.55, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <img
                            src="/media/retrato.webp"
                            alt="Retrato de José Luis Garcés Maldonado"
                            className="w-full h-72 object-cover"
                        />
                        <p className="mt-3 flex justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                            <span>El autor</span>
                            <span className="text-crimson">Quito</span>
                        </p>
                    </motion.div>
                </div>
            </div>

            <motion.button
                data-testid="hero-scroll-indicator"
                onClick={() => scrollToSection("#historia")}
                style={{ opacity: fade }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-subtle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6 }}
                aria-label="Desliza para leer"
            >
                Desliza para leer
                <motion.span
                    animate={{ y: [0, 6, 0] }}
                    transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                >
                    <ArrowDown size={14} />
                </motion.span>
            </motion.button>

            <Modal
                open={poemOpen}
                onClose={() => setPoemOpen(false)}
                label={`Poema ${featured.title}`}
                testId="hero-poem-modal"
            >
                <div className="p-8 sm:p-14">
                    <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-crimson">
                        {featured.book} · {featured.year}
                    </p>
                    <h3 className="mt-4 font-display text-4xl sm:text-5xl font-light text-ink">
                        {featured.title}
                    </h3>
                    <div className="mt-10 max-w-md">
                        {featured.lines.map((line, i) =>
                            line === "" ? (
                                <div key={i} className="h-6" />
                            ) : (
                                <motion.p
                                    key={i}
                                    className="font-display text-xl sm:text-2xl leading-[1.5] text-ink/90"
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.15 + i * 0.05, duration: 0.5 }}
                                >
                                    {line}
                                </motion.p>
                            )
                        )}
                    </div>
                    <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-6">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                            José Luis Garcés Maldonado
                        </p>
                        <button
                            data-testid="hero-poem-share-image-button"
                            onClick={shareFeatured}
                            disabled={sharing}
                            className="flex items-center gap-2 border border-crimson px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-crimson hover:bg-crimson hover:text-paper transition-colors disabled:opacity-50"
                        >
                            <ImageDown size={12} />
                            {sharing ? "Generando…" : "Compartir"}
                        </button>
                    </div>
                </div>
            </Modal>
        </section>
    );
};

export default Hero;
